import React, { useEffect, useState } from "react";
import { SwissDateInput } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/lib/formatDate";

const fmt = (n) => Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const AccountLedgerReport = () => {
  const companyId = localStorage.getItem("demoCompanyId");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const year = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${year}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!companyId) return;
    apiClient
      .get(`/companies/${companyId}/accounts`)
      .then((res) => setAccounts((res.data || []).sort((a, b) => String(a.code).localeCompare(String(b.code)))))
      .catch(() => {});
  }, [companyId]);

  const load = async (acc = accountId) => {
    if (!companyId || !acc) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(`/companies/${companyId}/account-ledger`, {
        params: { account_id: acc, start_date: startDate, end_date: endDate },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Fehler beim Laden des Kontoblatts.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const header = ["Datum", "Text", "Gegenkonto", "Soll", "Haben", "Saldo"];
    const rows = (data.rows || []).map((r) => [r.date, `"${(r.description || "").replace(/"/g, "'")}"`, r.counter_accounts, r.debit.toFixed(2), r.credit.toFixed(2), r.balance.toFixed(2)]);
    const lines = [
      `Kontoblatt ${data.account_id} ${data.account_name};${data.start_date} - ${data.end_date}`,
      `Anfangssaldo;;;;;${data.opening_balance.toFixed(2)}`,
      header.join(";"),
      ...rows.map((r) => r.join(";")),
      `Schlusssaldo;;;;;${data.closing_balance.toFixed(2)}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Kontoblatt_${data.account_id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm mt-4" data-testid="account-ledger-card">
      <div className="flex items-center flex-wrap gap-3 mb-4">
        <div className="min-w-[260px]">
          <Select
            value={accountId}
            onValueChange={(v) => {
              setAccountId(v);
              load(v);
            }}
          >
            <SelectTrigger data-testid="account-ledger-account-select">
              <SelectValue placeholder="Konto wählen …" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {accounts.map((a) => (
                <SelectItem key={a._id} value={a.code}>
                  {a.code} · {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SwissDateInput
          value={startDate}
          onChange={setStartDate}
          testid="account-ledger-start-date"
        />
        <span className="text-xs text-slate-500">bis</span>
        <SwissDateInput
          value={endDate}
          onChange={setEndDate}
          testid="account-ledger-end-date"
        />
        <Button type="button" size="sm" variant="outline" className="rounded-full px-3" onClick={() => load()} disabled={!accountId || loading} data-testid="account-ledger-load-button">
          {loading ? "Lade …" : "Anzeigen"}
        </Button>
        {data && (
          <Button type="button" size="sm" variant="outline" className="rounded-full px-3 ml-auto" onClick={exportCsv} data-testid="account-ledger-export-csv">
            CSV exportieren
          </Button>
        )}
      </div>

      {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 mb-3">{error}</div>}

      {!data && !error && (
        <div className="text-sm text-slate-400 py-8 text-center" data-testid="account-ledger-empty-hint">
          Wählen Sie ein Konto, um das Kontoblatt mit laufendem Saldo anzuzeigen.
        </div>
      )}

      {data && (
        <>
          <div className="flex items-center justify-between text-sm mb-2">
            <div className="font-medium text-slate-900" data-testid="account-ledger-title">
              Kontoblatt {data.account_id} {data.account_name && `· ${data.account_name}`}
            </div>
            <div className="text-xs text-slate-500">
              {formatDate(data.start_date)} – {formatDate(data.end_date)}
            </div>
          </div>
          <Table data-testid="account-ledger-table">
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Text</TableHead>
                <TableHead>Gegenkonto</TableHead>
                <TableHead className="text-right">Soll</TableHead>
                <TableHead className="text-right">Haben</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-slate-50/70" data-testid="account-ledger-opening-row">
                <TableCell colSpan={5} className="text-xs font-medium text-slate-600">Anfangssaldo</TableCell>
                <TableCell className="text-right font-medium tabular-nums">{fmt(data.opening_balance)}</TableCell>
              </TableRow>
              {data.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-slate-400" data-testid="account-ledger-no-rows">
                    Keine Bewegungen im Zeitraum.
                  </TableCell>
                </TableRow>
              )}
              {data.rows.map((r, i) => (
                <TableRow key={`${r.entry_id}-${i}`} data-testid={`account-ledger-row-${i}`}>
                  <TableCell className="whitespace-nowrap">{formatDate(r.date)}</TableCell>
                  <TableCell className="max-w-[320px] truncate">{r.description}</TableCell>
                  <TableCell className="font-mono text-xs">{r.counter_accounts}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.debit ? fmt(r.debit) : ""}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.credit ? fmt(r.credit) : ""}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-medium" data-testid="account-ledger-closing-row">
                <TableCell colSpan={3} className="text-xs text-slate-600">Schlusssaldo</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{fmt(data.total_debit)}</TableCell>
                <TableCell className="text-right tabular-nums text-xs">{fmt(data.total_credit)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(data.closing_balance)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}
    </Card>
  );
};
